import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
    ArrowRight,
    BadgeCheck,
    Calendar,
    CheckCircle2,
    ChevronDown,
    Globe,
    GraduationCap,
    HeartHandshake,
    HelpCircle,
    Sparkles,
    Timer,
} from "lucide-react";

/**
 * UniThink — Career Clarity Session Landing Page
 * React + Tailwind + Framer Motion (single file)
 *
 * How to use:
 * 1) Ensure Tailwind is set up.
 * 2) Install deps:
 *    npm i framer-motion lucide-react
 * 3) Render <CareerClarityLanding /> in your route/page.
 */

const cn = (...classes) => classes.filter(Boolean).join(" ");

const credibilityFacts = [
    {
        icon: Globe,
        title: "Global context",
        body: "Global higher education enrolment reached 264M; international student mobility ~6.9M — choices are exploding, so clarity matters.",
    },
    {
        icon: GraduationCap,
        title: "India scale",
        body: "AISHE 2021–22 reports 1,168 universities/university-level institutions, 45,473 colleges and ~4.33 crore enrolment.",
    },
    {
        icon: HelpCircle,
        title: "Career uncertainty",
        body: "OECD analysis of PISA 2022: ‘two in five’ students (age 15) across OECD countries have no clear career plans.",
    },
];

const highlights = [
    { icon: HeartHandshake, text: "1:1 personalised counselling with an experienced education counsellor" },
    { icon: Sparkles, text: "Direction based on your profile, preferences, and constraints" },
    { icon: Globe, text: "Country/course/pathway discussion (India + abroad options as relevant)" },
    { icon: CheckCircle2, text: "A practical next-steps plan you can act on immediately" },
];

const included = [
    "One-on-one personalised counselling",
    "Career clarity discussion + academic direction guidance",
    "Country/course/pathway exploration aligned to goals",
    "Second-opinion validation (if you already have a plan)",
    "Next steps: what to shortlist, research, prepare next",
];

const howItWorks = [
    {
        step: "Step 1",
        title: "Book + share your basics",
        desc: "Current education, grades, target intake, preferences, constraints — we keep it simple.",
        icon: Calendar,
    },
    {
        step: "Step 2",
        title: "1:1 clarity session",
        desc: "We cut the noise and focus on what will actually move your decision forward.",
        icon: Timer,
    },
    {
        step: "Step 3",
        title: "Action roadmap",
        desc: "You leave with a recommendation + a next-steps checklist (shortlists, research, prep).",
        icon: BadgeCheck,
    },
];

const outcomes = [
    "Clarity on the right academic direction + realistic pathways",
    "Less confusion and second-guessing (parents relax too)",
    "Better country/course alignment understanding (where applicable)",
    "Stronger decision confidence with trade-offs explained",
    "Practical diagnosis of what matters most in your next steps",
];

const faqs = [
    {
        q: "Is this session suitable if I am completely confused about my career?",
        a: "Yes. This is designed specifically for early-stage clarity and direction setting.",
    },
    {
        q: "Is this only for study abroad students?",
        a: "No. The session supports both India and international pathways as relevant.",
    },
    {
        q: "Will you provide university shortlisting in this session?",
        a: "You’ll get the shortlisting logic and directional guidance. Full shortlists are typically part of longer programs.",
    },
    {
        q: "Do you guarantee admission or outcomes?",
        a: "No. Admission decisions are made by institutions; UniThink provides guidance, planning, and support.",
    },
    {
        q: "What should I keep ready before the session?",
        a: "Your academic details, preferences (countries/courses if any), and constraints (budget, location, timeline).",
    },
    {
        q: "Is the session online or offline?",
        a: "Depends on scheduling and availability. You’ll see options at booking.",
    },
    {
        q: "Can I reschedule?",
        a: "Yes — define your rescheduling policy (e.g., 24-hour window) and display it at booking.",
    },
    {
        q: "What if I need ongoing support after the session?",
        a: "You can upgrade to a structured counselling program for multi-stage planning and execution.",
    },
];

function useLocalStorageBoolean(key, initial) {
    const [val, setVal] = useState(() => {
        try {
            const raw = localStorage.getItem(key);
            if (raw === null) return initial;
            return raw === "true";
        } catch {
            return initial;
        }
    });

    useEffect(() => {
        try {
            localStorage.setItem(key, String(val));
        } catch {
            // ignore
        }
    }, [key, val]);

    return [val, setVal];
}

function Pill({ children }) {
    return (
        <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs font-medium text-white/80 backdrop-blur">
            {children}
        </span>
    );
}

function SectionHeading({ eyebrow, title, desc }) {
    return (
        <div className="mx-auto max-w-3xl text-center">
            {eyebrow ? (
                <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-slate-200/10 bg-slate-900/40 px-3 py-1 text-xs font-semibold text-slate-200">
                    <Sparkles className="h-4 w-4" />
                    <span>{eyebrow}</span>
                </div>
            ) : null}
            <h2 className="text-balance text-2xl font-semibold tracking-tight text-slate-900 md:text-3xl">
                {title}
            </h2>
            {desc ? (
                <p className="mt-3 text-pretty text-sm leading-relaxed text-slate-600 md:text-base">
                    {desc}
                </p>
            ) : null}
        </div>
    );
}

function FAQItem({ q, a, defaultOpen = false }) {
    const [open, setOpen] = useState(defaultOpen);
    return (
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="flex w-full items-center justify-between gap-4 p-4 text-left"
                aria-expanded={open}
            >
                <div className="flex items-start gap-3">
                    <div className="mt-0.5 rounded-xl border border-slate-200 bg-slate-50 p-2">
                        <HelpCircle className="h-4 w-4 text-slate-700" />
                    </div>
                    <div>
                        <div className="text-sm font-semibold text-slate-900">{q}</div>
                        <div className="text-xs text-slate-500">Tap to {open ? "collapse" : "expand"}</div>
                    </div>
                </div>
                <ChevronDown
                    className={cn(
                        "h-5 w-5 text-slate-500 transition-transform",
                        open ? "rotate-180" : "rotate-0"
                    )}
                />
            </button>
            <motion.div
                initial={false}
                animate={{ height: open ? "auto" : 0, opacity: open ? 1 : 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden"
            >
                <div className="px-4 pb-4 pl-14 text-sm leading-relaxed text-slate-700">{a}</div>
            </motion.div>
        </div>
    );
}

function Toast({ show, onClose, title, body }) {
    useEffect(() => {
        if (!show) return;
        const t = setTimeout(onClose, 2600);
        return () => clearTimeout(t);
    }, [show, onClose]);

    return (
        <motion.div
            initial={{ opacity: 0, y: 18, scale: 0.98 }}
            animate={show ? { opacity: 1, y: 0, scale: 1 } : { opacity: 0, y: 18, scale: 0.98 }}
            transition={{ duration: 0.25 }}
            className="pointer-events-none fixed bottom-5 left-1/2 z-50 w-[min(92vw,420px)] -translate-x-1/2"
            aria-hidden={!show}
        >
            <div className="rounded-2xl border border-slate-200 bg-white/95 p-4 shadow-xl backdrop-blur">
                <div className="flex items-start gap-3">
                    <div className="rounded-xl border border-slate-200 bg-slate-50 p-2">
                        <BadgeCheck className="h-5 w-5 text-slate-800" />
                    </div>
                    <div className="min-w-0">
                        <div className="text-sm font-semibold text-slate-900">{title}</div>
                        <div className="mt-0.5 text-sm text-slate-600">{body}</div>
                    </div>
                </div>
            </div>
        </motion.div>
    );
}

function CTAButton({ label = "Book Session", onClick, variant = "primary" }) {
    const base =
        "inline-flex items-center justify-center gap-2 rounded-2xl px-5 py-3 text-sm font-semibold transition-all focus:outline-none focus:ring-2 focus:ring-offset-2";
    const styles =
        variant === "primary"
            ? "bg-slate-900 text-white hover:translate-y-[-1px] hover:bg-slate-800 focus:ring-slate-900"
            : "border border-slate-200 bg-white text-slate-900 hover:bg-slate-50 focus:ring-slate-400";

    return (
        <button type="button" onClick={onClick} className={cn(base, styles)}>
            {label}
            <ArrowRight className="h-4 w-4" />
        </button>
    );
}

function Field({ label, hint, children }) {
    return (
        <div className="space-y-1.5">
            <div className="flex items-end justify-between gap-3">
                <label className="text-sm font-semibold text-slate-900">{label}</label>
                {hint ? <span className="text-xs text-slate-500">{hint}</span> : null}
            </div>
            {children}
        </div>
    );
}

function Input({ ...props }) {
    return (
        <input
            {...props}
            className={cn(
                "w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900",
                "placeholder:text-slate-400",
                "focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-200"
            )}
        />
    );
}

function Textarea({ ...props }) {
    return (
        <textarea
            {...props}
            className={cn(
                "min-h-[110px] w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900",
                "placeholder:text-slate-400",
                "focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-200"
            )}
        />
    );
}

function Select({ children, ...props }) {
    return (
        <select
            {...props}
            className={cn(
                "w-full appearance-none rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900",
                "focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-200"
            )}
        >
            {children}
        </select>
    );
}

function BookingModal({ open, onClose, onBooked }) {
    const [name, setName] = useState("");
    const [phone, setPhone] = useState("");
    const [email, setEmail] = useState("");
    const [role, setRole] = useState("student");
    const [goal, setGoal] = useState("");

    useEffect(() => {
        if (!open) return;
        // small UX: reset goal only
        setGoal("");
    }, [open]);

    if (!open) return null;

    const submit = () => {
        const options = {
            key: process.env.REACT_APP_RAZORPAY_KEY,
            amount: 413000, // ₹3,500 in paise
            currency: "INR",
            name: "UniThink",
            description: "Career Clarity Session",
            handler: function (response) {
                // Payment was successful! Send data to Google Sheets
                fetch("https://script.google.com/macros/s/AKfycbw78zHkdmZkuyQcIKuow7TszMak6GG9boeb8isaRcX_mjBWw7w1rMJGeu08Jv54cZSA/exec", {
                    method: "POST",
                    mode: "no-cors",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        name: name,
                        phone: phone,
                        email: email,
                        role: role,
                        goal: goal,
                        payment_status: "Paid",
                        paymentId: response.razorpay_payment_id,
                        service: "Career Clarity Session",
                        amount: 4130
                    }),
                }).finally(() => {
                    alert("Payment Successful! We have received your request.");
                    window.location.href = "/";
                });
            },
            prefill: {
                name: name,
                email: email,
                contact: phone,
            },
            theme: { color: "#0f172a" },
            modal: {
                ondismiss: function () {
                    fetch("https://script.google.com/macros/s/AKfycbw78zHkdmZkuyQcIKuow7TszMak6GG9boeb8isaRcX_mjBWw7w1rMJGeu08Jv54cZSA/exec", {
                        method: "POST",
                        mode: "no-cors",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({
                            name: name,
                            phone: phone,
                            email: email,
                            role: role,
                            goal: goal,
                            payment_status: "Abandoned",
                            paymentId: "N/A",
                            service: "Career Clarity Session",
                            amount: 4130
                        }),
                    });
                }
            }
        };

        const rzp = new window.Razorpay(options);
        rzp.on('payment.failed', function (response) {
            fetch("https://script.google.com/macros/s/AKfycbw78zHkdmZkuyQcIKuow7TszMak6GG9boeb8isaRcX_mjBWw7w1rMJGeu08Jv54cZSA/exec", {
                method: "POST",
                mode: "no-cors",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    name: name,
                    phone: phone,
                    email: email,
                    role: role,
                    goal: goal,
                    payment_status: "Payment Failed",
                    paymentId: response.error.metadata.payment_id || "N/A",
                    service: "Career Clarity Session",
                    amount: 4130
                }),
            });
        });
        rzp.open();
    };

    const canSubmit = name.trim().length >= 2 && (phone.trim().length >= 8 || email.trim().includes("@"));

    return (
        <div className="fixed inset-0 z-50 grid place-items-center p-4">
            <div className="absolute inset-0 bg-slate-900/50 backdrop-blur" onClick={onClose} />
            <motion.div
                initial={{ opacity: 0, y: 16, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.2 }}
                className="relative w-full max-w-xl"
            >
                <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-2xl">
                    <div className="flex items-center justify-between gap-4 border-b border-slate-200 p-5">
                        <div>
                            <div className="text-sm font-semibold text-slate-900">Book Career Clarity Session</div>
                            <div className="text-xs text-slate-500">₹3,500 • 1:1 • Online/Offline as per slots</div>
                        </div>
                        <button
                            type="button"
                            onClick={onClose}
                            className="rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                        >
                            Close
                        </button>
                    </div>

                    <div className="grid gap-5 p-5 md:grid-cols-2">
                        <Field label="Name" hint="Required">
                            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g., Dhruv Singh" />
                        </Field>
                        <Field label="Role" hint="Who are you?">
                            <Select value={role} onChange={(e) => setRole(e.target.value)}>
                                <option value="student">Student</option>
                                <option value="parent">Parent</option>
                                <option value="teacher">Teacher</option>
                                <option value="working">Working Professional</option>
                            </Select>
                        </Field>
                        <Field label="Phone" hint="Phone or email required">
                            <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="e.g., 99xxxxxxx" />
                        </Field>
                        <Field label="Email" hint="Optional">
                            <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="e.g., name@gmail.com" />
                        </Field>
                        <div className="md:col-span-2">
                            <Field label="What do you need clarity on?" hint="Optional but helpful">
                                <Textarea
                                    value={goal}
                                    onChange={(e) => setGoal(e.target.value)}
                                    placeholder="Example: UG course confusion, India vs abroad, gap year, stream switch, budget + timeline…"
                                />
                            </Field>
                        </div>
                    </div>

                    <div className="flex flex-col gap-3 border-t border-slate-200 p-5 md:flex-row md:items-center md:justify-between">
                        <div className="text-xs text-slate-500">
                            Tip: We’ll confirm slot + mode on WhatsApp/Email. (Keep policy text here.)
                        </div>
                        <button
                            type="button"
                            onClick={submit}
                            disabled={!canSubmit}
                            className={cn(
                                "inline-flex items-center justify-center gap-2 rounded-2xl px-5 py-3 text-sm font-semibold",
                                canSubmit
                                    ? "bg-slate-900 text-white hover:bg-slate-800"
                                    : "cursor-not-allowed bg-slate-200 text-slate-500"
                            )}
                        >
                            Continue
                            <ArrowRight className="h-4 w-4" />
                        </button>
                    </div>
                </div>
            </motion.div>
        </div>
    );
}

function Nav() { }



export default function CareerClarityLanding() {
    const [modalOpen, setModalOpen] = useState(false);
    const [toastOn, setToastOn] = useState(false);
    const [socialProof, setSocialProof] = useLocalStorageBoolean("unithink_social_proof", true);

    const onBook = () => setModalOpen(true);

    const onBooked = () => {
        setToastOn(true);
        // If you want: send event to analytics here
    };

    const floatingFacts = useMemo(
        () => [
            { label: "Avg. decision time saved", value: "7–21 days" },
            { label: "Common confusion", value: "India vs Abroad" },
            { label: "Most asked", value: "Course + Career fit" },
        ],
        []
    );

    return (
        <div className="min-h-screen bg-slate-50 text-slate-900">
            <Nav />

            {/* HERO */}
            <div className="relative overflow-hidden bg-slate-950">
                <div className="absolute inset-0 opacity-80">
                    <div className="absolute -left-40 -top-40 h-[520px] w-[520px] rounded-full bg-white/10 blur-3xl" />
                    <div className="absolute -right-40 -bottom-40 h-[520px] w-[520px] rounded-full bg-white/10 blur-3xl" />
                    <div className="absolute left-1/2 top-16 h-[420px] w-[420px] -translate-x-1/2 rounded-full bg-white/5 blur-3xl" />
                </div>

                <div className="relative mx-auto max-w-6xl px-4 py-14 md:py-20">
                    <div className="grid items-center gap-10 md:grid-cols-2">
                        <div>
                            <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.35 }}
                                className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs font-semibold text-white/80"
                            >
                                <Sparkles className="h-4 w-4" />
                                <span>Less confusion. More confidence. Zero drama.</span>
                            </motion.div>

                            <motion.h1
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.35, delay: 0.05 }}
                                className="mt-5 text-balance text-4xl font-semibold tracking-tight text-white md:text-5xl"
                            >
                                Career Clarity Session
                            </motion.h1>

                            <motion.p
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.35, delay: 0.1 }}
                                className="mt-4 text-pretty text-base leading-relaxed text-white/75 md:text-lg"
                            >
                                A focused, one-on-one counselling session designed to help you make a confident academic and career decision—whether you need clarity, direction, or a strong second opinion.
                            </motion.p>

                            <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center">
                                <CTAButton label="Book Session" onClick={onBook} />
                                <CTAButton
                                    variant="secondary"
                                    label="View Pricing"
                                    onClick={() => {
                                        const el = document.getElementById("pricing");
                                        el?.scrollIntoView({ behavior: "smooth" });
                                    }}
                                />
                            </div>

                            <div className="mt-6 grid gap-3 sm:grid-cols-2">
                                {highlights.map((h, idx) => (
                                    <motion.div
                                        key={idx}
                                        initial={{ opacity: 0, y: 12 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.35, delay: 0.12 + idx * 0.05 }}
                                        className="rounded-2xl border border-white/10 bg-white/5 p-4"
                                    >
                                        <div className="flex items-start gap-3">
                                            <div className="mt-0.5 rounded-xl border border-white/10 bg-white/10 p-2">
                                                <h.icon className="h-4 w-4 text-white" />
                                            </div>
                                            <div className="text-sm font-medium text-white/85">{h.text}</div>
                                        </div>
                                    </motion.div>
                                ))}
                            </div>

                            {/* Gen Z humor microcopy */}
                            <div className="mt-6 text-xs text-white/60">
                                No, we won’t ask you “beta what do you want to become?” and then vanish. 😄
                            </div>
                        </div>

                        <div className="relative">
                            <motion.div
                                initial={{ opacity: 0, y: 12 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.4, delay: 0.1 }}
                                className="rounded-3xl border border-white/10 bg-white/5 p-5 shadow-2xl"
                            >
                                <div className="flex items-center justify-between">
                                    <div>
                                        <div className="text-sm font-semibold text-white">Quick Credibility Panel</div>
                                        <div className="text-xs text-white/60">Why guidance matters (in plain English)</div>
                                    </div>
                                    <Pill>Research-backed</Pill>
                                </div>

                                <div className="mt-4 grid gap-3">
                                    {credibilityFacts.map((c, idx) => (
                                        <div key={idx} className="rounded-2xl border border-white/10 bg-slate-950/30 p-4">
                                            <div className="flex items-start gap-3">
                                                <div className="mt-0.5 rounded-xl border border-white/10 bg-white/10 p-2">
                                                    <c.icon className="h-4 w-4 text-white" />
                                                </div>
                                                <div>
                                                    <div className="text-sm font-semibold text-white/90">{c.title}</div>
                                                    <div className="mt-1 text-sm leading-relaxed text-white/70">{c.body}</div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <div className="mt-4 rounded-2xl border border-white/10 bg-white/5 p-4">
                                    <div className="text-xs text-white/60">Tiny note:</div>
                                    <div className="mt-1 text-sm text-white/75">
                                        This session is built to reduce decisions based on incomplete info, peer pressure, or misinformation.
                                    </div>
                                </div>
                            </motion.div>

                            {/* floating chips */}
                            <div className="mt-4 grid gap-3 sm:grid-cols-3">
                                {floatingFacts.map((f, i) => (
                                    <div key={i} className="rounded-2xl border border-white/10 bg-white/5 p-4">
                                        <div className="text-xs text-white/60">{f.label}</div>
                                        <div className="mt-1 text-sm font-semibold text-white">{f.value}</div>
                                    </div>
                                ))}
                            </div>

                            {socialProof ? (
                                <div className="mt-4 rounded-2xl border border-white/10 bg-white/5 p-4">
                                    <div className="flex items-start justify-between gap-4">
                                        <div>
                                            <div className="text-sm font-semibold text-white">Parents-approved vibes ✅</div>
                                            <div className="mt-1 text-sm text-white/70">
                                                “Finally someone explained options without making it feel like a tuition class.”
                                            </div>
                                            <div className="mt-2 text-xs text-white/55">— a real parent (name withheld to avoid nazar 🧿)</div>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => setSocialProof(false)}
                                            className="rounded-xl border border-white/10 bg-white/10 px-3 py-2 text-xs font-semibold text-white/80 hover:bg-white/15"
                                        >
                                            Hide
                                        </button>
                                    </div>
                                </div>
                            ) : null}
                        </div>
                    </div>
                </div>
            </div>

            {/* WHAT THIS DOES */}
            <div className="mx-auto max-w-6xl px-4 py-14">
                <SectionHeading

                    title="What this service does"
                    desc="This session is designed to reduce confusion quickly and ensure you are not making decisions based on incomplete information, peer pressure, or misinformation."
                />

                <div className="mt-10 grid gap-6 md:grid-cols-2">
                    <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                        <div className="flex items-start gap-3">
                            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
                                <Sparkles className="h-5 w-5 text-slate-900" />
                            </div>
                            <div>
                                <div className="text-lg font-semibold">From “I like X” → to “This is my pathway”</div>
                                <p className="mt-2 text-sm leading-relaxed text-slate-600">
                                    We turn a broad interest into a best-fit direction with a clear set of next steps.
                                </p>
                            </div>
                        </div>

                        <div className="mt-5 grid gap-3">
                            {included.map((item, idx) => (
                                <div key={idx} className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-3">
                                    <CheckCircle2 className="mt-0.5 h-5 w-5 text-slate-800" />
                                    <div className="text-sm text-slate-700">{item}</div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                        <div className="flex items-start gap-3">
                            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
                                <BadgeCheck className="h-5 w-5 text-slate-900" />
                            </div>
                            <div>
                                <div className="text-lg font-semibold">Outcomes you can expect</div>
                                <p className="mt-2 text-sm leading-relaxed text-slate-600">
                                    No fluff. No gyaan overdose. Just clarity + action.
                                </p>
                            </div>
                        </div>

                        <div className="mt-5 grid gap-3">
                            {outcomes.map((o, idx) => (
                                <div key={idx} className="rounded-2xl border border-slate-200 bg-white p-4">
                                    <div className="flex items-start gap-3">
                                        <div className="mt-0.5 rounded-xl border border-slate-200 bg-slate-50 p-2">
                                            <CheckCircle2 className="h-4 w-4 text-slate-800" />
                                        </div>
                                        <div className="text-sm text-slate-700">{o}</div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                            <div className="text-xs font-semibold text-slate-600">For parents:</div>
                            <div className="mt-1 text-sm text-slate-700">
                                We’ll explain trade-offs clearly so decision-making doesn’t turn into a dinner-table debate.
                            </div>
                        </div>
                    </div>
                </div>

                {/* Mid CTA */}
                <div className="mt-10 rounded-3xl border border-slate-200 bg-slate-900 p-6 text-white shadow-sm md:p-8">
                    <div className="grid items-center gap-6 md:grid-cols-2">
                        <div>
                            <div className="text-xl font-semibold">Ready to stop overthinking?</div>
                            <div className="mt-2 text-sm text-white/75">
                                Book a Career Clarity Session and leave with a practical plan.
                            </div>
                        </div>
                        <div className="flex gap-3 md:justify-end">
                            <button
                                type="button"
                                onClick={onBook}
                                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-white px-5 py-3 text-sm font-semibold text-slate-900 hover:bg-slate-100"
                            >
                                Book Session
                                <ArrowRight className="h-4 w-4" />
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    const el = document.getElementById("faqs");
                                    el?.scrollIntoView({ behavior: "smooth" });
                                }}
                                className="inline-flex items-center justify-center rounded-2xl border border-white/20 bg-white/10 px-5 py-3 text-sm font-semibold text-white hover:bg-white/15"
                            >
                                See FAQs
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* HOW IT WORKS */}
            <div className="bg-white">
                <div className="mx-auto max-w-6xl px-4 py-14">
                    <SectionHeading
                        eyebrow="Simple 3-step flow"
                        title="How it works"
                        desc="Short, practical, and action-oriented — so you can move fast."
                    />

                    <div className="mt-10 grid gap-6 md:grid-cols-3">
                        {howItWorks.map((s, idx) => (
                            <motion.div
                                key={idx}
                                initial={{ opacity: 0, y: 12 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, amount: 0.2 }}
                                transition={{ duration: 0.35, delay: idx * 0.05 }}
                                className="rounded-3xl border border-slate-200 bg-slate-50 p-6"
                            >
                                <div className="flex items-start justify-between gap-3">
                                    <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-700">
                                        {s.step}
                                    </div>
                                    <div className="rounded-2xl border border-slate-200 bg-white p-3">
                                        <s.icon className="h-5 w-5 text-slate-900" />
                                    </div>
                                </div>
                                <div className="mt-4 text-lg font-semibold text-slate-900">{s.title}</div>
                                <div className="mt-2 text-sm leading-relaxed text-slate-600">{s.desc}</div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </div>

            {/* PRICING */}
            <div id="pricing" className="mx-auto max-w-6xl px-4 py-14">
                <SectionHeading
                    eyebrow="Pricing"
                    title="₹3,500 + 18% GST per session"
                    desc="Ideal for students seeking clarity or a second opinion before committing to a longer program."
                />

                <div className="mt-10 grid gap-6 md:grid-cols-2">
                    <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                        <div className="flex items-start justify-between gap-4">
                            <div>
                                <div className="text-sm font-semibold text-slate-600">What you get</div>
                                <div className="mt-1 text-2xl font-semibold text-slate-900">Clarity + Next Steps</div>
                                <div className="mt-2 text-sm text-slate-600">
                                    A focused session that reduces confusion and gives a practical roadmap.
                                </div>
                            </div>
                            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                                <div className="text-xs text-slate-500">Price</div>
                                <div className="text-lg font-semibold text-slate-900">₹3,500 + 18% GST</div>
                            </div>
                        </div>

                        <div className="mt-5 grid gap-3">
                            {highlights.map((h, idx) => (
                                <div key={idx} className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-3">
                                    <h.icon className="mt-0.5 h-5 w-5 text-slate-800" />
                                    <div className="text-sm text-slate-700">{h.text}</div>
                                </div>
                            ))}
                        </div>

                        <div className="mt-6 flex gap-3">
                            <button
                                type="button"
                                onClick={onBook}
                                className="inline-flex flex-1 items-center justify-center gap-2 rounded-2xl bg-slate-900 px-5 py-3 text-sm font-semibold text-white hover:bg-slate-800"
                            >
                                Book Session
                                <ArrowRight className="h-4 w-4" />
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    const el = document.getElementById("related");
                                    el?.scrollIntoView({ behavior: "smooth" });
                                }}
                                className="inline-flex items-center justify-center rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-900 hover:bg-slate-50"
                            >
                                Explore more
                            </button>
                        </div>

                        <div className="mt-4 text-xs text-slate-500">
                            *Rescheduling policy text goes here (e.g., up to 24 hours before slot).
                        </div>
                    </div>

                    <div className="rounded-3xl border border-slate-200 bg-slate-900 p-6 text-white shadow-sm">
                        <div className="text-sm font-semibold text-white/70">Quick check</div>
                        <div className="mt-1 text-2xl font-semibold">Is this for you?</div>

                        <div className="mt-5 grid gap-3">
                            {[
                                "You feel stuck between 2–3 options",
                                "Parents want a ‘logic’ not a ‘feeling’",
                                "You need a second opinion before spending big",
                                "You want a next-steps plan (not random advice)",
                            ].map((t, idx) => (
                                <div key={idx} className="rounded-2xl border border-white/15 bg-white/10 p-4">
                                    <div className="flex items-start gap-3">
                                        <CheckCircle2 className="mt-0.5 h-5 w-5 text-white" />
                                        <div className="text-sm text-white/85">{t}</div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="mt-6 rounded-2xl border border-white/15 bg-white/10 p-4">
                            <div className="text-sm font-semibold">Gen Z translation:</div>
                            <div className="mt-1 text-sm text-white/80">
                                We help you pick a path without spiralling into 40 tabs + 3 YouTube rabbit-holes.
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* RELATED */}
            <div id="related" className="bg-white">
                <div className="mx-auto max-w-6xl px-4 py-14">
                    <SectionHeading
                        eyebrow="Explore related services"
                        title="What’s next after clarity?"
                        desc="If you need structured long-term support, these are the usual next steps."
                    />

                    <div className="mt-10 grid gap-6 md:grid-cols-2">
                        {[
                            {
                                title: "Comprehensive Counselling Program",
                                desc: "Structured long-term planning — for students who want end-to-end guidance.",
                                chip: "Long-term",
                            },
                            {
                                title: "Application & Admission Support",
                                desc: "For students ready to apply — timelines, documents, essays, and submission support.",
                                chip: "Execution",
                            },
                            {
                                title: "India Admission Guidance",
                                desc: "India-specific admissions support — exams, counselling, colleges, and choices.",
                                chip: "India",
                            },
                            {
                                title: "Country-Focused Admission Support",
                                desc: "Italy/Germany and other destinations — eligibility, requirements, and application flow.",
                                chip: "Abroad",
                            },
                        ].map((c, idx) => (
                            <div key={idx} className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                                <div className="flex items-start justify-between gap-3">
                                    <div>
                                        <div className="text-lg font-semibold text-slate-900">{c.title}</div>
                                        <div className="mt-2 text-sm text-slate-600">{c.desc}</div>
                                    </div>
                                    <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-700">
                                        {c.chip}
                                    </span>
                                </div>
                                <div className="mt-5">
                                    <button
                                        type="button"
                                        onClick={onBook}
                                        className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-900 hover:bg-slate-50"
                                    >
                                        Enquire
                                        <ArrowRight className="h-4 w-4" />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Blog teaser */}
                    <div className="mt-10 rounded-3xl border border-slate-200 bg-slate-50 p-6 md:p-8">
                        <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
                            <div>
                                <div className="text-sm font-semibold text-slate-700">Recommended reading</div>
                                <div className="mt-2 text-xl font-semibold text-slate-900">UniThink Blog picks</div>
                                <div className="mt-2 text-sm text-slate-600">
                                    Hook them with value — then convert them with clarity.
                                </div>
                            </div>
                            <div className="grid gap-2 text-sm text-slate-700">
                                <div>• How to choose a course after Class 12 without regret</div>
                                <div>• India vs abroad: decision framework for UG/PG</div>
                                <div>• Mistakes students make during shortlisting—and how to avoid them</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* FAQ */}
            <div id="faqs" className="mx-auto max-w-6xl px-4 py-14">
                <SectionHeading eyebrow="FAQs" title="Questions people ask before booking" desc="Transparent answers. No sales fog." />

                <div className="mt-10 grid gap-4 md:grid-cols-2">
                    {faqs.map((f, idx) => (
                        <FAQItem key={idx} q={f.q} a={f.a} defaultOpen={idx === 0} />
                    ))}
                </div>
            </div>

            {/* FINAL CTA */}
            <div className="bg-slate-950">
                <div className="mx-auto max-w-6xl px-4 py-14">
                    <div className="rounded-3xl border border-white/10 bg-white/5 p-8 text-white">
                        <div className="grid items-center gap-8 md:grid-cols-2">
                            <div>
                                <div className="text-2xl font-semibold">Make your next academic decision with clarity.</div>
                                <div className="mt-3 text-sm text-white/75">
                                    Book your Career Clarity Session today and move forward with a practical plan.
                                </div>
                                <div className="mt-4 text-xs text-white/60">
                                    (Yes, we’ll be the calm voice in the chaos.)
                                </div>
                            </div>
                            <div className="flex flex-col gap-3 sm:flex-row md:justify-end">
                                <button
                                    type="button"
                                    onClick={onBook}
                                    className="inline-flex items-center justify-center gap-2 rounded-2xl bg-white px-6 py-3 text-sm font-semibold text-slate-900 hover:bg-slate-100"
                                >
                                    Book Session
                                    <ArrowRight className="h-4 w-4" />
                                </button>
                                <button
                                    type="button"
                                    onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                                    className="inline-flex items-center justify-center rounded-2xl border border-white/20 bg-white/10 px-6 py-3 text-sm font-semibold text-white hover:bg-white/15"
                                >
                                    Back to top
                                </button>
                            </div>
                        </div>
                    </div>


                </div>
            </div>

            <BookingModal open={modalOpen} onClose={() => setModalOpen(false)} onBooked={onBooked} />
            <Toast
                show={toastOn}
                onClose={() => setToastOn(false)}
                title="Request received"
                body="Nice. We’ll confirm your slot on WhatsApp/Email."
            />
        </div>
    );
}
